import loglevel from 'loglevel';
const log = loglevel.getLogger('table-client');
log.setDefaultLevel('INFO');
import { Buffer } from 'buffer';
import { combineLatest, from, fromEvent, merge, Subject, BehaviorSubject, of, connectable, Subscription, ReplaySubject, } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, scan, skip, tap, switchMap, bufferTime, startWith, share, } from 'rxjs/operators';
import { io } from 'socket.io-client';
import { ConnectionStatus, AuthStatus, } from './models.js';
import { compareKey } from './keys.js';
import { isDeepEqual } from 'remeda';
export class ProxyClient {
    nextSubscription = 1; // start at 1, not 0, to protect against truthy bugs when we do !sub
    subscriptionsParamsById = new Map();
    subscribe(key, bounds) {
        const socket = this.socket;
        if (!socket)
            throw new Error('Cannot do actions on the table client before a server is added.');
        const subscription = this.nextSubscription++;
        this.subscriptionsParamsById.set(subscription, { key, bounds });
        socket.emit('sub.add', subscription, key, bounds);
        log.debug('subscribe', subscription, key, bounds);
        return subscription;
    }
    unsubscribe(subscription) {
        this.subscriptionsParamsById.delete(subscription);
        const socket = this.socket;
        if (!socket)
            throw new Error('Cannot do actions on the table client before a server is added.');
        log.debug('unsubscribe', subscription);
        socket.emit('sub.del', subscription);
    }
    setBounds(subscription, bounds) {
        const socket = this.socket;
        if (!socket)
            throw new Error('Cannot do actions on the table client before a server is added.');
        const subscriptionObj = this.subscriptionsParamsById.get(subscription);
        if (!subscriptionObj)
            throw new Error(`Setting bounds of missing table: ${subscription}`);
        if (subscriptionObj.bounds.firstRow === bounds.firstRow &&
            subscriptionObj.bounds.windowSize === bounds.windowSize)
            return;
        if (bounds.windowSize === Infinity) {
            bounds.windowSize = Math.pow(2, 15);
        }
        log.debug('set bounds', subscription, bounds);
        subscriptionObj.bounds = bounds;
        socket.emit('sub.del', subscription);
        socket.emit('sub.add', subscription, subscriptionObj.key, subscriptionObj.bounds);
    }
    getDescriptors(_subscription) {
        // no-op
    }
    getTotalDescriptors(_subscription) {
        // no-op
    }
    getRowCount(_subscription) {
        throw new Error('Method not implemented.');
    }
    nextRequest = 0;
    defineObject(persist, definitions, affinity) {
        const socket = this.socket;
        if (!socket)
            throw new Error('Cannot do actions on the table client before a server is added.');
        const reqId = this.nextRequest++;
        log.info('creating object(s)', definitions);
        return this.connectionStatus$.pipe(filter((x) => x === ConnectionStatus.connected), first(), tap(() => socket.emit('table.create-object', persist, definitions, reqId, affinity)), switchMap(() => this.objectDefinitionResult$.pipe(filter((x) => x.reqId === reqId), first())));
    }
    getDiff(subscription) {
        const socket = this.socket;
        if (!socket)
            throw new Error('Cannot do actions on the table client before a server is added.');
        socket.emit('sub.get-diff', subscription);
        this.dataRequestSubject.next();
    }
    getSnapshot(subscription) {
        const socket = this.socket;
        if (!socket)
            throw new Error('Cannot do actions on the table client before a server is added.');
        socket.emit('sub.get-snapshot', subscription);
        this.dataRequestSubject.next();
    }
    getTotals(subscription) {
        const socket = this.socket;
        if (!socket)
            throw new Error('Cannot do actions on the table client before a server is added.');
        // actually table.key.ready-for-totals is not implemented on the table-ws side and is not needed
        // it will be sent by server automatically
        socket.emit('table.key.ready-for-totals', subscription);
    }
    addAccessCookie(cookie) {
        // send to src if present
        log.debug(`sending cookie`);
        const socket = this.socket;
        if (!socket)
            throw new Error('Cannot do actions on the table client before a server is added.');
        socket.emit('session.add-access-cookie', cookie);
    }
    emitLogin() {
        const socket = this.socket;
        if (!socket)
            throw new Error('Cannot do actions on the table client before a server is added.');
        if (!this._auth || typeof this._auth === 'string') {
            socket.emit('session.login', this.token, { binarySnapshots: true, useSuperuser: this.useSuperuser });
        }
        else {
            const { clientId, clientSecret } = this.token;
            socket.emit('session.login-api', clientId, clientSecret, {
                binarySnapshots: true,
                useSuperuser: this.useSuperuser,
            });
        }
    }
    socket;
    keys$;
    _keys;
    get keys() {
        return this._keys;
    }
    endOfData$;
    descriptor$;
    rowCount$;
    updates$;
    diffs$;
    totalsUpdates$;
    totalsDescriptor$;
    objectDefinitionResult$;
    connectionStatus$;
    authStatus$;
    definition$;
    accessCookieResult$;
    shutdownSubscription = new Subscription();
    // tslint:disable-next-line:variable-name
    _auth;
    get token() {
        return this._auth;
    }
    set token(token) {
        if (token || this._auth) {
            // either a token is provided, or a previous token exists that we want to log out
            log.debug('logged in using token or secret', token);
            this._auth = token;
            if (this.socket) {
                this.emitLogin();
            }
        }
        else {
            this._auth = token;
        }
    }
    _useSuperuser = true;
    get useSuperuser() {
        return this._useSuperuser;
    }
    set useSuperuser(val) {
        log.debug(`useSuperuser ${val}`);
        this._useSuperuser = val;
        if (this._auth && this.socket) {
            this.emitLogin();
        }
    }
    socketChanged$ = new BehaviorSubject(undefined);
    setServer(address) {
        const socket = io(address, {
            transports: ['websocket'],
        });
        this.socket = socket;
        const status$ = fromEvent(socket, 'status').pipe(distinctUntilChanged((x, y) => isDeepEqual(x, y)));
        const tableStatus$ = status$.pipe(map((x) => x.wsStatus));
        tableStatus$.pipe(filter((x) => x.authorized === null || x.authorized === undefined)).subscribe(() => {
            if (this._auth)
                this.emitLogin();
        });
        // send ping so all events rebind
        this.socketChanged$.next();
        socket.open();
    }
    constructor(address, auth, useSuperuser = true) {
        this._useSuperuser = useSuperuser;
        if (typeof window !== 'undefined') {
            window['table'] = {
                client: this,
                tables: [],
                get keys() {
                    return this.keys;
                },
            };
        }
        const socket$ = connectable(this.socketChanged$.pipe(switchMap(() => of(this.socket)), filter((x) => Boolean(x))), {
            connector: () => new ReplaySubject(1),
        });
        this.shutdownSubscription.add(socket$.connect());
        function forEachSocket(proj) {
            const obs$ = socket$.pipe(switchMap((socket) => proj('', socket)));
            return obs$;
        }
        const statusPkg$ = connectable(socket$.pipe(switchMap((socket) => fromEvent(socket, 'status').pipe(distinctUntilChanged((x, y) => isDeepEqual(x, y))))));
        this.shutdownSubscription.add(statusPkg$.connect());
        const connectionStatus$ = (this.connectionStatus$ = connectable(socket$.pipe(switchMap((socket) => {
            const proxyDisconnected$ = fromEvent(socket, 'disconnect').pipe(map(() => ConnectionStatus.disconnected));
            const proxyConnectionFault$ = fromEvent(socket, 'error').pipe(tap((err) => log.warn('Connection error to proxy', 'Error:', err)), map(() => ConnectionStatus.disconnected));
            const proxyConnected$ = fromEvent(socket, 'connect').pipe(map(() => ConnectionStatus.connected));
            const proxyStatus$ = merge(proxyConnected$, proxyConnectionFault$, proxyDisconnected$);
            return proxyStatus$;
        }), startWith(ConnectionStatus.disconnected), tap((x) => log.info('Connection status change', x))), { connector: () => new ReplaySubject(1) }));
        this.shutdownSubscription.add(connectionStatus$.connect());
        this.authStatus$ = combineLatest({
            statusPkg: statusPkg$,
            connectionStatus: this.connectionStatus$,
        }).pipe(map(({ statusPkg: { wsStatus }, connectionStatus }) => {
            if (connectionStatus !== ConnectionStatus.connected)
                return undefined;
            if (wsStatus.authorized === false)
                return AuthStatus.badToken;
            // if (!beStatus.connected) return ConnectionStatus.noTable;
            if (!wsStatus.authorized)
                return AuthStatus.uninitialized;
            return AuthStatus.authenticated;
        }), startWith(AuthStatus.uninitialized), tap((x) => log.info('Auth status change', x)), share({
            connector: () => new ReplaySubject(1),
            resetOnComplete: true,
            resetOnError: true,
            resetOnRefCountZero: true,
        }));
        // auto-resubscribe to tables on connect
        this.shutdownSubscription.add(forEachSocket((src, socket) => fromEvent(socket, 'connect').pipe(tap(() => {
            for (const [subscription, subscriptionObj] of this.subscriptionsParamsById.entries()) {
                socket.emit('sub.add', subscription, subscriptionObj.key, subscriptionObj.bounds);
            }
        }))).subscribe());
        const newKeysLog$ = new Subject();
        this.shutdownSubscription.add(newKeysLog$
            .pipe(bufferTime(250), filter((x) => x.length !== 0))
            .subscribe((keys) => log.debug('[keys]: Got new key(s):', keys.length === 1 ? keys[0] : keys)));
        const keysPerSocket$ = socket$.pipe(switchMap((socket) => {
            const connectedPulses$ = statusPkg$.pipe(map((x) => x.beStatus.connected), distinctUntilChanged(), filter((x) => x === false), tap(() => log.info('Got reset pulse')));
            const addKey$ = fromEvent(socket, 'table.key');
            const clearAllKeys$ = fromEvent(socket, 'table.clearAllKeys');
            const initKeys$ = fromEvent(socket, 'table.initKeys');
            const deleteKey$ = fromEvent(socket, 'table.key.removed');
            const pulses$ = merge(connectedPulses$.pipe(skip(1), map(() => ({ type: 'clear' }))), clearAllKeys$.pipe(map(() => ({ type: 'clear' }))), initKeys$.pipe(map((x) => ({ keys: x, type: 'init' }))), addKey$.pipe(map((x) => ({ key: x, type: 'add' }))), deleteKey$.pipe(map((x) => ({ key: x, type: 'delete' }))));
            const keys$ = pulses$.pipe(scan((acc, x) => {
                if (x.type === 'clear') {
                    log.debug('[keys]: Cleared keys');
                    acc = [];
                }
                else if (x.type === 'init') {
                    log.debug('[keys]: New keys init', x.keys);
                    acc = [...x.keys];
                }
                else if (x.type === 'add') {
                    acc = [...acc, x.key];
                    newKeysLog$.next(x.key);
                }
                else if (x.type === 'delete') {
                    const deleteIdx = acc.findIndex((y) => compareKey(x.key, y));
                    if (deleteIdx !== -1) {
                        acc = [...acc];
                        acc.splice(deleteIdx, 1);
                    }
                    log.debug('[keys]: Deleted key', x);
                }
                else {
                    throw new Error(`Encountered unknown key event: ${x.type}`);
                }
                return acc;
            }, []), debounceTime(100));
            return keys$;
        }));
        const allKeys$ = connectable(keysPerSocket$, { connector: () => new ReplaySubject(1) });
        this.shutdownSubscription.add(allKeys$.pipe( /*auditTime(500)*/).subscribe((keys) => {
            log.debug('[keys]: Keys list now has length', keys.length);
        }));
        this.keys$ = allKeys$;
        this.shutdownSubscription.add(allKeys$.connect());
        this.descriptor$ = forEachSocket((src, socket) => fromEvent(socket, 'table.descriptors').pipe(map((x) => ({
            subscription: x.subscription.id,
            descriptors: x.descriptors.descriptorsArr,
        })))).pipe(share({ resetOnRefCountZero: true }));
        this.rowCount$ = forEachSocket((src, socket) => fromEvent(socket, 'table.total-rows').pipe(map((x) => ({
            subscription: x.subscription.id,
            rowCount: x.totalRows,
        })))).pipe(share({ resetOnRefCountZero: true }));
        const binarySnapshotUpdates$ = forEachSocket((_src, socket) => fromEvent(socket, 'table.snapshot.binary').pipe(map((x) => ({
            subscription: x.subscription.id,
            // todo: change this next line
            updates: from({
                *[Symbol.iterator]() {
                    const conflatedRowUpdate = {
                        subscription: x.subscription,
                        payload: undefined, // hacky but this value will never be seen
                        row: undefined, // hacky but this value will never be seen
                        rowId: undefined,
                    };
                    const binary = Buffer.from(x.snapshot);
                    const snapshotLen = binary.length;
                    let offset = 0;
                    let row = x.subscription.bounds.firstRow;
                    while (offset < snapshotLen) {
                        const rowId = binary.readUInt32LE(offset);
                        offset += 4;
                        const _pmask = binary.readUInt32LE(offset);
                        offset += 4;
                        const rowLen = binary.readUInt32LE(offset);
                        offset += 4;
                        const buf = binary.slice(offset, offset + rowLen);
                        offset += rowLen;
                        conflatedRowUpdate.payload = buf;
                        conflatedRowUpdate.rowId = rowId;
                        conflatedRowUpdate.row = row++;
                        yield conflatedRowUpdate;
                    }
                },
            }),
        })))).pipe(share({ resetOnRefCountZero: true }));
        this.updates$ = binarySnapshotUpdates$; // merge(snapshotUpdates$, binarySnapshotUpdates$);
        const binaryDiffs$ = forEachSocket((src, socket) => fromEvent(socket, 'table.snapshot.diff').pipe(map((x) => {
            const orderedRowIds = [
                ...{
                    *[Symbol.iterator]() {
                        const binary = Buffer.from(x.diff.orderedRowsIds);
                        let offset = 0;
                        const len = binary.length;
                        while (offset < len) {
                            const rowId = binary.readUInt32LE(offset);
                            offset += 4;
                            yield rowId;
                        }
                    },
                },
            ];
            Object.freeze(orderedRowIds);
            const rowIdToRowIndex = new Map(orderedRowIds.map((id, i) => [id, i + x.subscription.bounds.firstRow]));
            Object.freeze(rowIdToRowIndex);
            const deletedRowIds = new Set([
                ...{
                    *[Symbol.iterator]() {
                        const binary = Buffer.from(x.diff.deletedRowsIds);
                        let offset = 0;
                        const len = binary.length;
                        while (offset < len) {
                            const rowId = binary.readUInt32LE(offset);
                            offset += 4;
                            yield rowId;
                        }
                    },
                },
            ]);
            Object.freeze(deletedRowIds);
            return {
                subscription: x.subscription.id,
                orderedRowIds,
                rowIdToRowIndex,
                deletedRowIds,
                // todo: change this next line
                updates: from({
                    *[Symbol.iterator]() {
                        const conflatedRowUpdate = {
                            subscription: x.subscription,
                            payload: undefined, // hacky but this value will never be seen
                            row: undefined, // hacky but this value will never be seen
                            rowId: undefined,
                        };
                        const binary = Buffer.from(x.diff.updatedRows);
                        const snapshotLen = binary.length;
                        let offset = 0;
                        while (offset < snapshotLen) {
                            const rowId = binary.readUInt32LE(offset);
                            offset += 4;
                            const _pmask = binary.readUInt32LE(offset);
                            offset += 4;
                            const rowLen = binary.readUInt32LE(offset);
                            offset += 4;
                            const buf = binary.slice(offset, offset + rowLen);
                            offset += rowLen;
                            conflatedRowUpdate.payload = buf;
                            conflatedRowUpdate.rowId = rowId;
                            conflatedRowUpdate.row = rowIdToRowIndex.get(rowId);
                            yield conflatedRowUpdate;
                        }
                    },
                }),
            };
        }))).pipe(share({ resetOnRefCountZero: true }));
        this.diffs$ = binaryDiffs$;
        this.totalsUpdates$ = forEachSocket((src, socket) => fromEvent(socket, 'table.totals-data').pipe(map((x) => ({
            subscription: x.subscription.id,
            payload: Buffer.from(x.totalsData.buf),
        })))).pipe(share({ resetOnRefCountZero: true }));
        this.totalsDescriptor$ = forEachSocket((src, socket) => fromEvent(socket, 'table.totals-descriptors').pipe(map((x) => ({
            subscription: x.subscription.id,
            descriptors: x.descriptors.descriptorsArr,
        })))).pipe(share({ resetOnRefCountZero: true }));
        this.definition$ = forEachSocket((src, socket) => fromEvent(socket, 'table.json-definition').pipe(map((x) => ({
            subscription: x.subscription.id,
            objects: x.jsonDefinition.create,
        })))).pipe(share({ resetOnRefCountZero: true }));
        this.objectDefinitionResult$ = forEachSocket((src, socket) => fromEvent(socket, 'table.create-object.result').pipe(map((x) => ({ comment: x.message, reqId: x.reqId, success: x.success, src })))).pipe(share({ resetOnRefCountZero: true }));
        this.endOfData$ = forEachSocket((src, socket) => fromEvent(socket, 'table.end-of-data').pipe(map((x) => ({
            subscription: x.subscription.id,
        })))).pipe(share({ resetOnRefCountZero: true }));
        this.accessCookieResult$ = forEachSocket((src, socket) => fromEvent(socket, 'session.access-cookie-result').pipe(tap((x) => log.debug('got access token result', x)), map((x) => ({
            ...x,
            src,
        })))).pipe(share({ resetOnRefCountZero: true }));
        this.historicDescriptor$ = forEachSocket((src, socket) => fromEvent(socket, 'table.historic-descriptors').pipe(map((x) => ({
            subscription: x.subscription.id,
            descriptors: x.descriptors.descriptorsArr,
        })))).pipe(share({ resetOnRefCountZero: true }));
        this.historicUpdate$ = forEachSocket((src, socket) => fromEvent(socket, 'table.historic-data').pipe(map((x) => ({
            subscription: x.subscription.id,
            payload: x.data,
            seqNum: x.seqNum,
        })))).pipe(share({ resetOnRefCountZero: true }));
        // set token before addServer, so token will be available for login
        this.token = auth; // activate login
        // add default src
        if (address)
            this.setServer(address);
    }
    historicDescriptor$;
    historicUpdate$;
    historicSubscriptions = new Map();
    requestHistoricData(key, windowSize = 100) {
        const socket = this.socket;
        if (!socket)
            throw new Error('Cannot do actions on the table client before a server is added.');
        // reuse same Id for historic data
        const subscription = this.historicSubscriptions.get(key.sym) ?? this.nextSubscription++;
        this.historicSubscriptions.set(key.sym, subscription);
        const bounds = {
            windowSize,
            firstRow: 0,
        };
        socket.emit('sub.get-historic', subscription, key, bounds);
        return subscription;
    }
    dataRequestSubject = new Subject();
    dataRequest$ = this.dataRequestSubject.asObservable();
}
//# sourceMappingURL=proxyClient.js.map